import { Link } from 'react-router';
import { cn } from '@/lib/cn';
import { formatDateRange, formatLira, ticketRule } from '../format';
import type { TicketEvent } from '../api';

const phaseBadges: Record<string, { text: string; className: string }> = {
  running: { text: 'ETKİNLİK AÇIK', className: 'bg-emerald-500/15 text-emerald-300' },
  upcoming: { text: 'YAKINDA', className: 'bg-sky-500/15 text-sky-300' },
  ended: { text: 'BİTTİ', className: 'bg-surface-700/60 text-surface-300' },
};

/**
 * One event as a card: the partner, the rule, and the clock.
 *
 * The rule is the biggest thing on the card because it is the only number that
 * changes what a member does. The ticket count they would earn is deliberately
 * NOT advertised — it depends on their deposit, so any figure here would be
 * somebody else's.
 */
export function TicketEventCard({ event }: { event: TicketEvent }) {
  const badge = phaseBadges[event.phase] ?? phaseBadges.ended!;

  return (
    <article
      data-tilt
      className="ticket-card relative overflow-hidden rounded-[var(--radius-card)] border border-surface-800 p-5 sm:p-6"
      style={{ boxShadow: `inset 0 1px 0 0 ${event.siteBrandColor}22` }}
    >
      <div className="flex items-center gap-3">
        <span
          className="grid size-11 shrink-0 place-items-center rounded-full p-[2px]"
          style={{
            background: `linear-gradient(160deg, ${event.siteBrandColor}66, transparent 70%)`,
          }}
        >
          {event.siteIconUrl ? (
            <img
              src={event.siteIconUrl}
              alt=""
              width={40}
              height={40}
              loading="lazy"
              className="size-10 rounded-full object-cover"
            />
          ) : (
            <span className="grid size-10 place-items-center rounded-full bg-surface-800 text-sm font-bold text-surface-300">
              {event.siteName.slice(0, 1)}
            </span>
          )}
        </span>

        <div className="min-w-0 flex-1">
          <p className="text-xs uppercase tracking-widest text-surface-500">{event.siteName}</p>
          <h2 className="truncate font-display text-lg font-semibold tracking-tight text-surface-50">
            {event.title}
          </h2>
        </div>

        <span className={cn('shrink-0 rounded-full px-2.5 py-1 text-[0.6875rem] font-bold', badge.className)}>
          {badge.text}
        </span>
      </div>

      {event.description ? (
        <p className="mt-4 line-clamp-2 text-sm leading-relaxed text-surface-400">
          {event.description}
        </p>
      ) : null}

      {event.prizes.length > 0 ? (
        <p className="mt-4 text-sm text-surface-300">
          <span className="text-surface-500">Ödül: </span>
          {event.prizes[0]!.label}
          {event.prizes.length > 1 ? ` ve ${event.prizes.length - 1} ödül daha` : ''}
        </p>
      ) : null}

      <div className="mt-5 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-widest text-surface-500">Bilet kuralı</p>
          <p className="mt-1 font-display text-2xl font-bold tracking-tight text-brand-400">
            {ticketRule(event.depositThreshold, event.ticketsPerThreshold)}
          </p>
          <p className="mt-1 text-xs text-surface-500">{formatDateRange(event.startsAtUtc, event.endsAtUtc)}</p>
        </div>

        <p className="text-right text-xs text-surface-500">
          Eşik
          <span className="mt-1 block font-mono text-sm text-surface-300">
            {formatLira(event.depositThreshold)}
          </span>
        </p>
      </div>

      <Link
        to={`/ticket-events/${event.slug}`}
        className="gf-button gf-button-secondary mt-5 w-full sm:w-auto"
      >
        {event.phase === 'running' ? 'Katıl' : 'Detayı gör'}
        <span aria-hidden>↗</span>
      </Link>
    </article>
  );
}
